import React, { Component } from 'react';
import { observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';

import { AppConstants } from '../../../constants';
import { ILoanAccountActivity } from '../../../interfaces';

import AccountMarkedDelinquent from './AccountMarkedDelinquent';
import ComplianceChargeFinalized from './ComplianceChargeFinalized';
import LoanCreated from './LoanCreated';
import LoanDeleted from './LoanDeleted';
import LoanEdited from './LoanEdited';
import Note from './Note';
import OwnerAssigned from './OwnerAssigned';
import PaymentLogged from './PaymentLogged';

const { LOAN_ACCOUNT_ACTIVITY_TYPES } = AppConstants;

// c.f. common-activities/Activity: one entry per activity type the API can serve.
const ACTIVITY_COMPONENTS: { [key: string]: any } = {
  [LOAN_ACCOUNT_ACTIVITY_TYPES.ACCOUNT_MARKED_DELINQUENT]: AccountMarkedDelinquent,
  [LOAN_ACCOUNT_ACTIVITY_TYPES.COMPLIANCE_CHARGE_FINALIZED]: ComplianceChargeFinalized,
  [LOAN_ACCOUNT_ACTIVITY_TYPES.LOAN_CREATED]: LoanCreated,
  [LOAN_ACCOUNT_ACTIVITY_TYPES.LOAN_DELETED]: LoanDeleted,
  [LOAN_ACCOUNT_ACTIVITY_TYPES.LOAN_EDITED]: LoanEdited,
  [LOAN_ACCOUNT_ACTIVITY_TYPES.NOTE]: Note,
  [LOAN_ACCOUNT_ACTIVITY_TYPES.OWNER_ASSIGNED]: OwnerAssigned,
  [LOAN_ACCOUNT_ACTIVITY_TYPES.PAYMENT_LOGGED]: PaymentLogged,
};

interface IProps {
  activity: ILoanAccountActivity;
  // Only a note does anything with this; the other types are records of something that happened and
  // have nothing to delete.
  onNoteDeleted: () => Promise<void>;
}

/**
 * Whether this build has a component for the activity's type. The list filters on this rather than
 * rendering the activity and letting it return null, which would leave its wrapper behind as an
 * empty bordered row in the feed.
 */
export function isRenderableActivity (activity: ILoanAccountActivity) {
  return !!ACTIVITY_COMPONENTS[activity.type];
}

@autoBindMethods
@observer
class Activity extends Component<IProps> {
  public render () {
    const { activity, onNoteDeleted } = this.props
      , ActivityComponent = ACTIVITY_COMPONENTS[activity.type]
      ;

    // A type this build doesn't know about is skipped rather than breaking the rest of the feed.
    if (!ActivityComponent) {
      // tslint:disable-next-line no-console
      console.warn(`Invalid loan account activity type '${activity.type}'`);
      return null;
    }

    // Passed explicitly rather than spread, as the type components themselves do
    return <ActivityComponent activity={activity} onNoteDeleted={onNoteDeleted} />;
  }
}

export default Activity;
